/**
 * "Cover the fees" calculator for the donate form.
 *
 * Given the donor's intended donation, computes the gross amount they'd
 * pay so that — after GoCardless and (if applicable) TrusteeKit fees are
 * deducted — the charity nets the donor's intended amount in full.
 *
 * Fee constants:
 *   GoCardless charity rate (Open Banking + Direct Debit):  0.75% + 15p
 *   TrusteeKit app_fee on Gift Aid donations:               1.25%, capped
 *                                                            at £2,000 base
 *
 * The Gift Aid app_fee constants are imported from the GoCardless
 * billing-requests module so cover math always tracks the same source
 * of truth used at payment-creation time. If the GC partner contract
 * changes, update GC_FEE_PERCENT/GC_FEE_FIXED_PENCE here.
 *
 * Math.ceil on the gross keeps the charity from being short by sub-pence
 * rounding — donor pays at most 1p extra, which the charity also receives.
 */

import { GIFT_AID_FEE_RATE, GIFT_AID_FEE_CAP_PENCE } from './gift-aid-fee';

export const GC_FEE_PERCENT = 0.0075;
export const GC_FEE_FIXED_PENCE = 15;

export interface FeeCover {
  basePence: number;
  totalPence: number;
  coverPence: number;
  giftAid: boolean;
}

export function computeFeeCover(basePence: number, giftAid: boolean): FeeCover {
  if (basePence <= 0) {
    return { basePence: 0, totalPence: 0, coverPence: 0, giftAid };
  }

  let totalPence: number;
  if (!giftAid) {
    // gross - (gross * 0.0075 + 15) = base
    totalPence = Math.ceil(
      (basePence + GC_FEE_FIXED_PENCE) / (1 - GC_FEE_PERCENT),
    );
  } else if (basePence <= GIFT_AID_FEE_CAP_PENCE) {
    // gross - (gross * 0.0075 + 15) - (gross * 0.0125) = base
    totalPence = Math.ceil(
      (basePence + GC_FEE_FIXED_PENCE) /
        (1 - GC_FEE_PERCENT - GIFT_AID_FEE_RATE),
    );
  } else {
    // App_fee fixed at the cap; only the GC percentage scales with gross.
    const cappedAppFee = Math.round(GIFT_AID_FEE_CAP_PENCE * GIFT_AID_FEE_RATE);
    totalPence = Math.ceil(
      (basePence + GC_FEE_FIXED_PENCE + cappedAppFee) / (1 - GC_FEE_PERCENT),
    );
  }

  return {
    basePence,
    totalPence,
    coverPence: totalPence - basePence,
    giftAid,
  };
}

export function formatPence(pence: number): string {
  if (pence < 100) return `${pence}p`;
  const pounds = pence / 100;
  return pounds % 1 === 0 ? `£${pounds.toFixed(0)}` : `£${pounds.toFixed(2)}`;
}
